import React, { ChangeEvent, FC, SyntheticEvent, useCallback, useRef, useState } from 'react';

import classNames from 'classnames';
import padStart from 'lodash/padStart';

import { Popup, PopupTheme } from '../components/Popup/Popup';

import './AudioPlayer.scss';

type Props = {
    readonly previewUrl: string;
};

const AudioPlayer: FC<Props> = ({ previewUrl }) => {
    const playerRef = useRef<HTMLAudioElement>(null);
    const toggleRef = useRef<HTMLElement>(null);

    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);

    const onPlay = useCallback(() => setPlaying(true), []);
    const onPause = useCallback(() => setPlaying(false), []);

    const onTimeUpdate = useCallback((event: SyntheticEvent<HTMLAudioElement>) => {
        const player = event.target as HTMLAudioElement;
        const progress = player.currentTime;
        setProgress(progress);
    }, []);

    const onSeek = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const player = playerRef.current;

        if (player) {
            const value = event.target.valueAsNumber;
            player.currentTime = isNaN(value) ? 0 : value;
        }
    }, []);

    const onLoadedMetaData = useCallback((event: SyntheticEvent<HTMLAudioElement>) => {
        const player = event.target as HTMLAudioElement;
        const duration = player.duration;
        setDuration(duration);
    }, []);

    const pauseAllPlayers = useCallback(() => {
        const allPlayers = document.querySelectorAll('audio');
        allPlayers.forEach(p => p.pause());
    }, []);

    const togglePlay = useCallback(() => {
        const player = playerRef.current;

        if (!player) {
            return;
        }

        if (player.ended) {
            player.load();
        }

        if (player.paused) {
            pauseAllPlayers();
            player.play();
        } else {
            player.pause();
        }
    }, [pauseAllPlayers]);

    return (
        <div className="playerControls icn-preview">
            <audio
                ref={playerRef}
                src={previewUrl}
                className="audioPlayer"
                preload="none"
                onPlay={onPlay}
                onPause={onPause}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onLoadedMetaData}
            />
            <Popup
                className={classNames('playerProgress', playing && 'active')}
                theme={PopupTheme.PLAYER}
                visible={playing}
                target={toggleRef}
                placement="top"
                portal={false}
            >
                <Time value={progress} />
                <input type="range" value={progress} min={0} max={duration} onChange={onSeek} />
                <Time value={duration} />
            </Popup>
            <i ref={toggleRef} className={classNames(playing && 'audio_playing')} onClick={togglePlay} />
        </div>
    );
};

export default AudioPlayer;

type TimeProps = {
    readonly value: number;
};

const Time: FC<TimeProps> = ({ value }) => {
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value - minutes * 60);
    return (
        <span>
            {padStart(String(minutes), 1, '0')}:{padStart(String(seconds), 2, '0')}
        </span>
    );
};
